// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { ApiBase } from './apiBase';
import type { ApiRequestConfig } from './apiBase';
import type {
  SnapshotCreateRequest,
  SnapshotCreateResponse,
  SnapshotDiffResponse,
  SnapshotGetResponse,
  SnapshotListResponse,
} from '@/types/snapshots';
import type { AboutDepsResponse, DevUpgradeLangchainResponse } from '@/types/about';

export class ApiMisc extends ApiBase {
  // ── 推荐审计快照（QUAL T3/P2-45：自裸 fetch 收敛至 api 单例）──
  // batch8.1：401 必须抑制强制登出——后端对无 paper-workbench 资产的账号
  // 返回 401（资产所有权语义），快照保存是后台尽力持久化，不应因此清掉
  // 整个登录会话（曾致 Free 用户打开科研工作台即被登出）。
  saveRecommendationAuditSnapshot(payload: unknown) {
    return this.client.post(
      '/v1/runtime-assets/recommendation-audit-snapshot',
      payload,
      {
        suppressUnauthorizedRedirect: true,
        suppressErrorToast: true,
      } as ApiRequestConfig,
    );
  }

  clearRecommendationAuditSnapshot(purgeHistory: boolean) {
    return this.client.delete(
      '/v1/runtime-assets/recommendation-audit-snapshot',
      { params: purgeHistory ? { purge_history: true } : undefined },
    );
  }

  // Files
  async uploadFile(file: File, description?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    return this.client.post('/v1/files/upload', formData, {
      headers: {
        'Content-Type': false,
      },
    });
  }

  async deleteFile(fileId: string) {
    return this.client.delete(`/v1/files/${fileId}`);
  }

  // Health
  async healthCheck() {
    return this.client.get('/health');
  }

  // ----------------------------
  // Agent-Flow: Snapshots
  // ----------------------------

  /**
   * 创建快照（保存画布 JSON）。
   * - 后端按 user_id 隔离数据，因此必须携带 Authorization
   */
  async createSnapshot<TPayload = unknown>(payload: SnapshotCreateRequest<TPayload>) {
    return this.client.post<SnapshotCreateResponse<TPayload>>('/snapshots', payload);
  }

  /** 获取快照列表（默认 limit=200，后端上限 2000） */
  async listSnapshots(params?: { limit?: number }) {
    return this.client.get<SnapshotListResponse>('/snapshots', { params });
  }

  /** 获取单个快照（用于回溯加载） */
  async getSnapshot<TPayload = unknown>(snapshotId: string) {
    return this.client.get<SnapshotGetResponse<TPayload>>(
      `/snapshots/${encodeURIComponent(snapshotId)}`
    );
  }

  /** 删除快照 */
  async deleteSnapshot(snapshotId: string) {
    return this.client.delete<{ deleted: boolean; snapshot_id: string }>(
      `/snapshots/${encodeURIComponent(snapshotId)}`
    );
  }

  /**
   * 快照 diff（后端同时返回两份 payload + unified diff 文本）
   * - 前端可用 left/right payload 做"结构化可视化 diff"
   */
  async diffSnapshots<TPayload = unknown>(leftId: string, rightId: string) {
    return this.client.get<SnapshotDiffResponse<TPayload>>(
      `/snapshots/${encodeURIComponent(leftId)}/diff/${encodeURIComponent(rightId)}`
    );
  }

  // ----------------------------
  // About / Dev tools
  // ----------------------------

  async aboutDeps() {
    return this.client.get<AboutDepsResponse>('/about/deps');
  }

  async devUpgradeLangchain(payload?: { apply?: boolean }) {
    return this.client.post<DevUpgradeLangchainResponse>(
      '/dev/upgrade-langchain',
      payload ?? { apply: true }
    );
  }

  // ----------------------------
  // Bug Feedback (User)
  // ----------------------------

  async submitBugReport(data: {
    title: string;
    description: string;
    steps_to_reproduce?: string;
    severity?: string;
    screenshot_file_ids?: string[];
    page_url?: string;
    browser_info?: string;
  }) {
    return this.client.post('/v1/feedback/bugs', data);
  }

  async listMyBugReports(params?: { limit?: number; offset?: number }) {
    return this.client.get('/v1/feedback/bugs', { params });
  }

  async getMyBugReport(bugId: string) {
    return this.client.get(`/v1/feedback/bugs/${bugId}`);
  }

  // ----------------------------
  // Manim video expiration
  // ----------------------------

  async expireVideos(sessionId: string, fileIds: string[]) {
    return this.client.post('/internal/v1/manim/expire-videos', {
      session_id: sessionId,
      file_ids: fileIds,
    });
  }
}
